import React, { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiError, formatCurrency } from "@/lib/api";
import { toast } from "sonner";

const STATUS_COLOR = { active: "#1E5CBA", completed: "#059669", "on-hold": "#d97706", cancelled: "#94a3b8" };

export const PortfolioMap = () => {
  const [data, setData] = useState(null);
  const mapRef = useRef(null);
  const mapObj = useRef(null);

  useEffect(() => {
    api.get("/portfolio/map")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    if (!data || !mapRef.current || mapObj.current) return;
    const map = L.map(mapRef.current, { scrollWheelZoom: false, zoomAnimation: false }).setView([5.15, 46.2], 5);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 18,
    }).addTo(map);
    const bounds = [];
    data.mapped.forEach((p) => {
      p.locations.forEach((loc) => {
        const val = p.contract_value || p.budget || 0;
        const radius = Math.min(22, Math.max(7, Math.sqrt(val) / 100));
        const marker = L.circleMarker([loc.lat, loc.lng], {
          radius, weight: 2, color: STATUS_COLOR[p.status] || "#1E5CBA",
          fillColor: STATUS_COLOR[p.status] || "#1E5CBA", fillOpacity: 0.35,
        }).addTo(map);
        marker.bindPopup(`
          <div style="min-width:210px;font-size:12px">
            <div style="font-weight:600">${p.name}</div>
            <div style="color:#64748b">${p.client || ""} · ${loc.label}</div>
            <table style="margin-top:6px;width:100%;font-size:11px">
              <tr><td style="color:#64748b">Budget</td><td style="text-align:right;font-weight:600">${formatCurrency(p.budget)}</td></tr>
              <tr><td style="color:#64748b">Actual cost</td><td style="text-align:right">${formatCurrency(p.actual_cost)}</td></tr>
              <tr><td style="color:#64748b">Open commitments</td><td style="text-align:right">${formatCurrency(p.open_commitments)}</td></tr>
              <tr><td style="color:#64748b">Progress</td><td style="text-align:right">${p.progress_pct || 0}%</td></tr>
            </table>
            ${loc.resolved_from !== "gps" ? '<div style="margin-top:4px;color:#94a3b8;font-size:10px">Approximate — plotted from ' + loc.resolved_from.replaceAll("_", " ") + "</div>" : ""}
          </div>`);
        bounds.push([loc.lat, loc.lng]);
      });
    });
    if (bounds.length) map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
    mapObj.current = map;
    requestAnimationFrame(() => { try { map.invalidateSize(); } catch { /* unmounted */ } });
    return () => { try { map.remove(); } catch { /* already gone */ } mapObj.current = null; };
  }, [data]);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="portfolio-map">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold text-slate-900">Portfolio map</h2>
        {data && (
          <span className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500" data-testid="portfolio-map-counts">
            {data.totals.mapped} of {data.totals.projects} projects plotted
          </span>
        )}
        <div className="ml-auto flex items-center gap-3 text-[10px]">
          {Object.entries(STATUS_COLOR).map(([k, c]) => (
            <span key={k} className="inline-flex items-center gap-1 text-slate-500">
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: c }} /> {k}
            </span>
          ))}
        </div>
      </div>
      <div ref={mapRef} className="h-[440px] w-full rounded-lg" data-testid="portfolio-map-canvas" />
      {data?.unmapped?.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-800" data-testid="portfolio-map-unmapped">
          <span className="font-semibold">{data.unmapped.length} project(s) without a location:</span>{" "}
          {data.unmapped.map((p) => p.name).join(" · ")}
          <span className="ml-1 text-amber-600">— add locations in the project form to plot them.</span>
        </div>
      )}
    </div>
  );
};

export default PortfolioMap;
